import { PropsWithChildren } from 'react';
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider as QueryProvider,
  useQuery,
} from '@taxdown/zeno-sama-query';
import AuthService from 'Services/auth';
import RecordsService from 'Services/records';
import NotificationService, { AlertTypes } from 'Services/notification';
import { getErrorDetail } from 'Services/errors';
import { store } from 'Store';
import { setTaxSelectedAction } from 'Store/modules/tax/actions';
import { setStageAction } from 'Store/modules/validations/actions';
import { QueryKeys } from 'Types/common';

export const queryClient = new QueryClient({
  mutationCache: new MutationCache({
    onError: (error, variables, context, mutation) => {
      if (!mutation?.options?.onError) {
        NotificationService.sendNotification(getErrorDetail(error), AlertTypes.error);
      }
    },
  }),
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (!query.meta?.onError) {
        NotificationService.sendNotification(getErrorDetail(error), AlertTypes.error);
      }
    },
  }),
});

export const SuspenseFetching = (): null => {
  useQuery(
    [QueryKeys.GET_USER_STAGE],
    async () => {
      try {
        const currentUser = await AuthService.currentUser();
        if (!currentUser) {
          return { stage: null };
        }
        const data = await RecordsService.getRecords({
          current: true,
        });
        const info = data?.data[0];
        const record = info?.item;
        const { recordId, taxYear, stage } = record;
        if (recordId) {
          store.dispatch(setTaxSelectedAction({ recordId, taxYear }));
        }
        if (stage) {
          store.dispatch(setStageAction(stage));
        }
        return { stage: null };
      } catch (error) {
        return { stage: null };
      }
    },
    {
      refetchOnWindowFocus: false,
      suspense: true,
    }
  );
  return null;
};

export const QueryClientProvider = ({ children }: PropsWithChildren): JSX.Element => (
  <QueryProvider queryClient={queryClient} showDevtools={process.env.NODE_ENV === 'development'}>
    <SuspenseFetching />
    {children}
  </QueryProvider>
);
